import { useQuery } from "@tanstack/react-query";
import type * as C from "@zooi/contracts";
import { q } from "./api.ts";
import { number, percent, usd } from "./format.ts";
import { GATE_LABELS as G } from "./labels.ts";
import { Empty, ErrorText, Pill, Section, Stat } from "./ui.tsx";
import { Skeleton } from "./ui/skeleton.tsx";

// Settings › Gate (plan M5 step 5e): the stage A gate an operator reads across
// workspaces. Counts, ratios and codes only; unknown stays unknown.

function Value({ value }: { value: C.GateValue }) {
  const v = G.value[value];
  return <Pill tone={v.tone} status>{v.label}</Pill>;
}

export function GatePanel() {
  const report = useQuery(q.gate);
  if (report.isLoading) return <Skeleton shape={["stats", "list"]} />;
  if (!report.data) return <ErrorText error={report.error} />;
  const r = report.data;
  const m = r.metrics;
  return <div className="z-flow">
    <Section title={G.title} note={G.note}>
      <p className="z-small z-muted">{G.scope}</p>
      <div className="z-stats">
        <Stat label={G.partners} value={number(r.partners.passing)} of={number(r.partners.required)} note={G.partnersNote(r.partners.products)} />
        <Stat label={G.active} value={number(m.active_products)} note={G.window} />
        <Stat label={G.loop} value={percent(m.loop.rate)} note={G.ofCount(m.loop.on_time, m.loop.total)} />
        <Stat label={G.execution} value={percent(m.execution.rate)} note={G.ofCount(m.execution.live, m.execution.approved)} />
        <Stat label={G.readings} value={percent(m.readings.rate)} note={G.ofCount(m.readings.measured, m.readings.matured)} />
        <Stat label={G.cost} value={usd(m.cost_usd)} note={G.window} />
      </div>
    </Section>
    <Section title={G.products} count={r.products.length}>
      {r.products.length ? <div className="z-scroll-x"><table className="z-table">
        <thead><tr>
          <th>{G.product}</th>
          {G.week.map((week, i) => <th key={i}>{G.weekLoop} · {week}</th>)}
          {G.week.map((week, i) => <th key={`a${i}`}>{G.attention} · {week}</th>)}
          <th>{G.live}</th><th>{G.measuredReadings}</th><th>{G.measured}</th><th>{G.willingness}</th>
        </tr></thead>
        <tbody>{r.products.map((p) => <tr key={p.product_id}>
          <td><div>{p.product}</div><div className="z-small z-muted">{p.workspace}{p.internal && <> · {G.zero}</>}</div></td>
          {p.weeks.map((w, i) => <td key={i} title={G.weekRange(w.from, w.to)}><Value value={w.loop} /></td>)}
          {p.weeks.map((w, i) => <td key={`a${i}`} title={G.weekRange(w.from, w.to)}><Value value={w.attention} />{w.attention_minutes !== null && <div className="z-small z-muted">{G.minutes(w.attention_minutes)}</div>}</td>)}
          <td><Value value={p.execution.value} /><div className="z-small z-muted">{number(p.execution.verified_live)}</div></td>
          <td><Value value={p.result.value} /><div className="z-small z-muted">{number(p.result.measured_readings)}</div></td>
          <td>{p.internal ? <span className="z-small z-muted">{G.zero}</span> : <Value value={p.measured} />}</td>
          <td><span className="z-small z-muted">{G.fromInterviews}</span></td>
        </tr>)}</tbody>
      </table></div> : <Empty>{G.none}</Empty>}
    </Section>
    <Section title={G.engines} note={G.enginesNote}>
      {r.engines.length ? <table className="z-table">
        <thead><tr><th>{G.engine}</th><th>{G.submissions}</th><th>{G.passed}</th><th>{G.passRate}</th></tr></thead>
        <tbody>{r.engines.map((e) => <tr key={e.engine ?? "-"}><td>{e.engine ?? <span className="z-muted">{G.undeclared}</span>}</td><td>{number(e.attempts)}</td><td>{number(e.accepted)}</td><td>{percent(e.rate)}</td></tr>)}</tbody>
      </table> : <p className="z-small z-muted">–</p>}
    </Section>
  </div>;
}
